import { Link } from 'react-router-dom';
import { useGetPtoRdQueueQuery, type PtoRdQueueImport, type PtoRdQueueIngest } from '@shared/api';
import { Table } from '@shared/ui/Table';
import { plural } from './format';
import { Cell, Code, Mine, Muted, Panel, Section, SectionTitle, Small, Tone } from './styles';

function importStatus(i: PtoRdQueueImport) {
  switch (i.status) {
    case 'recognizing':
      return (
        <Tone $tone="accent">
          распознаётся · {i.files_done} из {i.files}
        </Tone>
      );
    case 'applying':
      return <Tone $tone="accent">загружается в реестр</Tone>;
    default:
      return <Tone $tone="muted">в очереди · впереди {i.ahead ?? 0}</Tone>;
  }
}

function ingestStatus(r: PtoRdQueueIngest) {
  if (r.ingest_status === 'parsing') return <Tone $tone="accent">индексируется</Tone>;
  return <Tone $tone="muted">в очереди · впереди {r.ahead ?? 0}</Tone>;
}

/** Ревизии одного пакета, ждущие своей очереди внутри пакета, — одной строкой. */
interface WaitingGroup {
  key: string;
  importId: string | null;
  name: string;
  mine: boolean;
  by: string | null;
  count: number;
}

/**
 * Очередь обработки РД: пакеты (распознавание, применение) и индексация
 * ревизий — что идёт сейчас, кто загрузил, кто впереди. Ревизии пакета,
 * ждущие своей очереди внутри пакета (пакет индексирует по одной), — одной
 * строкой на пакет. Чужие загрузки у инженера без сквозного доступа сервер
 * обезличивает (пакет и проект скрыты). `compact` — не показывать пустую очередь.
 */
export function RdQueuePanel({ compact = false }: { compact?: boolean }) {
  const { data } = useGetPtoRdQueueQuery(undefined, {
    pollingInterval: 10000,
    skipPollingIfUnfocused: true,
  });
  const me = data?.mine;
  const imports = data?.imports ?? [];
  const ingest = (data?.ingest ?? []).filter((r) => !r.waiting_in_import);
  const waiting = new Map<string, WaitingGroup>();
  for (const r of data?.ingest ?? []) {
    if (!r.waiting_in_import) continue;
    // У чужих загрузок пакет скрыт (import_id = null) — такие группируем по автору.
    const key = r.import_id ?? `other:${r.uploaded_by ?? ''}`;
    const group = waiting.get(key) ?? {
      key,
      importId: r.import_id,
      name: r.import_name ?? (r.import_id ? 'Пакет РД' : 'Пакет другого инженера'),
      mine: r.uploaded_by === me,
      by: r.uploaded_by_name,
      count: 0,
    };
    group.count += 1;
    waiting.set(key, group);
  }
  const empty = imports.length === 0 && ingest.length === 0 && waiting.size === 0;
  if (compact && empty) return null;

  return (
    <Section>
      <SectionTitle>Очередь обработки РД</SectionTitle>
      <Panel>
        {empty ? (
          <Muted>Очередь пуста — новые загрузки обрабатываются сразу.</Muted>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>Что</th>
                <th>Кто загрузил</th>
                <th>Состояние</th>
              </tr>
            </thead>
            <tbody>
              {imports.map((i) => (
                <Mine key={i.id} $mine={i.created_by === me}>
                  <Cell>
                    {i.created_by === me ? (
                      <Link to={`/agents/pto/rd-import/${i.id}`}>{i.source_name}</Link>
                    ) : (
                      i.source_name
                    )}
                    <div>
                      <Small>
                        пакет РД · {i.files} {plural(i.files, ['файл', 'файла', 'файлов'])}
                      </Small>
                    </div>
                  </Cell>
                  <Cell>{i.created_by_name ?? '—'}</Cell>
                  <Cell>{importStatus(i)}</Cell>
                </Mine>
              ))}
              {ingest.map((r) => (
                <Mine key={r.revision_id} $mine={r.uploaded_by === me}>
                  <Cell>
                    {r.project_id === null ? (
                      // Обезличенная чужая загрузка: шифра и проекта нет.
                      r.document_code
                    ) : (
                      <>
                        <Code>{r.document_code}</Code>
                        {r.rev_label ? <Small> · ревизия {r.rev_label}</Small> : null}
                      </>
                    )}
                    {r.project_name || r.import_name ? (
                      <div>
                        <Small>
                          {[r.project_name, r.import_name ? `из пакета «${r.import_name}»` : null]
                            .filter(Boolean)
                            .join(' · ')}
                        </Small>
                      </div>
                    ) : null}
                  </Cell>
                  <Cell>{r.uploaded_by_name ?? '—'}</Cell>
                  <Cell>{ingestStatus(r)}</Cell>
                </Mine>
              ))}
              {[...waiting.values()].map((w) => (
                <Mine key={`waiting:${w.key}`} $mine={w.mine}>
                  <Cell>
                    {w.mine && w.importId ? (
                      <Link to={`/agents/pto/rd-import/${w.importId}`}>{w.name}</Link>
                    ) : (
                      w.name
                    )}
                    <div>
                      <Small>
                        ещё {w.count}{' '}
                        {plural(w.count, ['комплект ждёт', 'комплекта ждут', 'комплектов ждут'])}{' '}
                        индексации — пакет индексирует по одному, чтобы не занимать очередь других
                        инженеров
                      </Small>
                    </div>
                  </Cell>
                  <Cell>{w.by ?? '—'}</Cell>
                  <Cell>
                    <Tone $tone="muted">ждёт в пакете</Tone>
                  </Cell>
                </Mine>
              ))}
            </tbody>
          </Table>
        )}
      </Panel>
    </Section>
  );
}
